import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileArchive, ExternalLink } from 'lucide-react'
import { dataTransferApi, type DataTransferLog } from '../../api/endpoints'
import Badge from '../../components/ui/Badge'
import Pagination from '../../components/ui/Pagination'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 10

type FilterType = 'all' | 'import' | 'export'

const FILTERS: { value: FilterType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'import', label: 'Imports' },
  { value: 'export', label: 'Exports' },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatResults(row: DataTransferLog): string {
  if (row.transfer_type === 'import') {
    const parts: string[] = []
    if (row.created_count != null) parts.push(`${row.created_count} created`)
    if (row.duplicate_count != null && row.duplicate_count > 0) parts.push(`${row.duplicate_count} duplicates`)
    if (row.error_count != null && row.error_count > 0) parts.push(`${row.error_count} errors`)
    return parts.length > 0 ? parts.join(', ') : '--'
  }
  // export
  if (row.record_count != null) {
    return `${row.record_count} records exported`
  }
  return '--'
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export default function DataTransfersPage() {
  const [filter, setFilter] = useState<FilterType>('all')
  const [offset, setOffset] = useState(0)

  const params: Record<string, string> = {
    limit: String(PAGE_SIZE),
    offset: String(offset),
  }
  if (filter !== 'all') {
    params.type = filter
  }

  const { data, isLoading } = useQuery({
    queryKey: ['data-transfers', filter, offset],
    queryFn: () => dataTransferApi.list(params),
  })

  const transfers = data?.data ?? []
  const total = data?.total ?? 0

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileArchive size={22} className="text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-900">Import / Export History</h1>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-1 mb-5">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => {
              setFilter(f.value)
              setOffset(0)
            }}
            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
              filter === f.value
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <LoadingState message="Loading transfer history..." />
      )}

      {/* Empty state */}
      {!isLoading && transfers.length === 0 && (
        <EmptyState
          icon={FileArchive}
          title="No transfers yet"
          description="When you import or export leads as CSV, each transfer will appear here for reference."
        />
      )}

      {/* Content */}
      {!isLoading && transfers.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-gray-100">
            {transfers.map((row) => (
              <div key={row.id} className="p-4">
                {/* Type badge + file name */}
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={row.transfer_type === 'import' ? 'info' : 'success'}>
                    {row.transfer_type === 'import' ? 'Import' : 'Export'}
                  </Badge>
                  <span className="text-sm font-medium text-gray-900 truncate">{row.file_name}</span>
                </div>

                {/* Records + results */}
                <p className="text-xs text-gray-600 mb-1">
                  {row.record_count != null ? `${row.record_count.toLocaleString()} records` : 'No record count'}
                  {' — '}
                  {formatResults(row)}
                </p>

                {/* By + date */}
                <p className="text-xs text-gray-500 mb-2">
                  {row.initiated_by_name || 'Unknown'} &middot; {formatDate(row.created_at)}
                </p>

                {/* Download link */}
                {row.has_file && (
                  <div className="flex items-center gap-2">
                    <a
                      href={`/api/data-transfers/${row.id}/download`}
                      className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium min-h-[44px]"
                    >
                      Download <ExternalLink size={12} />
                    </a>
                    {row.download_count != null && row.max_downloads != null && (
                      <span className="text-[10px] text-gray-400">
                        {row.download_count}/{row.max_downloads} used
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">File Name</th>
                  <th className="px-4 py-3 text-right">Records</th>
                  <th className="px-4 py-3">Results</th>
                  <th className="px-4 py-3">By</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">File</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transfers.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <Badge variant={row.transfer_type === 'import' ? 'info' : 'success'}>
                        {row.transfer_type === 'import' ? 'Import' : 'Export'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-900 font-medium max-w-[200px] truncate">
                      {row.file_name}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 tabular-nums">
                      {row.record_count != null ? row.record_count.toLocaleString() : '--'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatResults(row)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.initiated_by_name || '--'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {formatDate(row.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {row.has_file ? (
                        <div className="flex flex-col gap-0.5">
                          <a
                            href={`/api/data-transfers/${row.id}/download`}
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                          >
                            Download <ExternalLink size={12} />
                          </a>
                          {row.download_count != null && row.max_downloads != null && (
                            <span className="text-[10px] text-gray-400">
                              {row.download_count}/{row.max_downloads} downloads used
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">--</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 pb-3">
            <Pagination
              total={total}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </div>
        </div>
      )}
    </div>
  )
}
